import { Badge, Button, Icon, Text } from '@shopify/polaris';
import { DiscountIcon, GiftCardIcon } from '@shopify/polaris-icons';
import BrandAvatar, { PlusLogo } from '@/components/BrandAvatar';
import { GuideTaskKey, IGuideStatus } from '@/types/welcome';
import { ClaimGiftWrap, DiscountBanner, ProgressSection, TaskList, TaskRow } from './styled';

export interface ITask {
  key: GuideTaskKey;
  title: string;
  description: string;
  path: string;
  ctaLabel: string;
  isNew?: boolean;
  avatarStack?: Array<'claude' | 'chatgpt' | 'plus'>;
}

export function capitalize(str: string) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

interface IGuideContentProps {
  guide: IGuideStatus;
  tasks: ITask[];
  completedCount: number;
  pct: number;
  activeKey: string | null;
  pendingTaskKey: GuideTaskKey | null;
  setExpandedKey: (key: string | null) => void;
  handleNavigate: (task: ITask) => void;
  onClaimGift: () => void;
  showTrialOffer: boolean;
}

const GuideContent = ({
  guide,
  tasks,
  completedCount,
  pct,
  activeKey,
  pendingTaskKey,
  setExpandedKey,
  handleNavigate,
  onClaimGift,
  showTrialOffer,
}: IGuideContentProps) => (
  <>
    {/* Discount banner */}
    <DiscountBanner $completed={guide.guideCompleted}>
      <div className="icon-wrap">
        <Icon source={DiscountIcon} tone={guide.guideCompleted ? 'success' : 'caution'} />
      </div>
      <span className="text">
        {guide.guideCompleted ? (
          <>
            Congratulations! Enjoy 20% off{showTrialOffer ? ' and 14 trial days' : ''} for your first subscription month.
            <br />
            <Text as="span" tone="subdued">
              Offer is automatically applied in Subscription Plans page, or use code: <strong>WELCOME20</strong>
            </Text>
          </>
        ) : (
          <>
            <strong>Welcome gift:</strong> Complete onboarding guide to unlock <strong>20% off first month</strong>
            {showTrialOffer ? (
              <>
                {' '}
                and <strong>x2 trial days</strong>
              </>
            ) : null}
            .
          </>
        )}
      </span>
    </DiscountBanner>

    {guide.guideCompleted && (
      <ClaimGiftWrap>
        <Button variant="primary" tone="success" fullWidth icon={GiftCardIcon} onClick={onClaimGift}>
          Claim your welcome gift
        </Button>
      </ClaimGiftWrap>
    )}

    {/* Progress */}
    <ProgressSection>
      <div className="label">
        {completedCount} of {tasks.length} tasks completed
      </div>
      <div className="bar-wrap">
        <div className="bar-fill" style={{ width: `${pct}%` }} />
      </div>
    </ProgressSection>

    {/* Task list */}
    <TaskList $locked={!!pendingTaskKey}>
      {tasks.map((task) => {
        const fieldKey = `guidTask${capitalize(task.key)}` as keyof IGuideStatus;
        const isPending = pendingTaskKey === task.key;
        const done = !!guide[fieldKey] || isPending;
        const isExpanded = activeKey === task.key;

        return (
          <TaskRow
            key={task.key}
            $done={done}
            $expanded={isExpanded}
            $pending={isPending}
            onClick={() => (isExpanded ? handleNavigate(task) : setExpandedKey(task.key))}
          >
            <div className="row-header">
              {done ? (
                <div className="check" onClick={(e) => e.stopPropagation()}>
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </div>
              ) : (
                <div className="check" onClick={(e) => e.stopPropagation()} />
              )}
              <span className="task-title">{task.title}</span>
              {(task.avatarStack || task.isNew) && !done && (
                <div className="row-right">
                  {task.avatarStack && (
                    <div className="avatar-stack" aria-hidden="true">
                      {task.avatarStack.map((b) =>
                        b === 'plus' ? (
                          <BrandAvatar key={b} brand="other" size="sm" logo={PlusLogo} />
                        ) : (
                          <BrandAvatar key={b} brand={b} size="sm" />
                        ),
                      )}
                    </div>
                  )}
                  {task.isNew && <Badge tone="info">New</Badge>}
                </div>
              )}
            </div>
            {isExpanded && (
              <div className="task-body">
                <p className="desc">{task.description}</p>
                <div onClick={(e) => e.stopPropagation()}>
                  <Button size="slim" onClick={() => handleNavigate(task)}>
                    {task.ctaLabel}
                  </Button>
                </div>
              </div>
            )}
          </TaskRow>
        );
      })}
    </TaskList>
  </>
);

export default GuideContent;
